import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  InputBase,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FeedbackDetailView from "./FeedbackDetailView";

import { GroupedFeedbackSession, PaginationInfo } from "../../api/derin/interface";
import { getGroupedFeedbackSessions } from "../../api/derin";
import {
  derinColors,
  derinTypography,
  derinSpacing,
  derinInputStyles,
} from "./theme";

// Helper to format date from ISO string
const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }) + " at " + date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const FeedbackSection: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<GroupedFeedbackSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedFeedback, setSelectedFeedback] = useState<GroupedFeedbackSession | null>(null);
  const userDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  const fetchFeedbacks = useCallback(async (page: number = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const params: { page: number; limit: number; status?: "New" | "Reviewed"; userName?: string } = {
        page,
        limit: 20,
      };
      if (statusFilter && statusFilter !== "all") {
        params.status = statusFilter as "New" | "Reviewed";
      }
      if (debouncedUserSearch) {
        params.userName = debouncedUserSearch;
      }

      const result = await getGroupedFeedbackSessions(params);
      if (page === 1) {
        setFeedbacks(result.data);
      } else {
        setFeedbacks((prev) => [...prev, ...result.data]);
      }
      setPagination(result.pagination);
    } catch (err) {
      console.error("Error fetching feedback sessions:", err);
      setError("Failed to load feedback sessions");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [statusFilter, debouncedUserSearch]);

  // Debounce user search input
  useEffect(() => {
    if (userDebounceTimer.current) {
      clearTimeout(userDebounceTimer.current);
    }
    userDebounceTimer.current = setTimeout(() => {
      setDebouncedUserSearch(userSearch);
    }, 400);
    return () => {
      if (userDebounceTimer.current) {
        clearTimeout(userDebounceTimer.current);
      }
    };
  }, [userSearch]);

  // Fetch when filters change
  useEffect(() => {
    fetchFeedbacks(1);
  }, [fetchFeedbacks]);

  const handleLoadMore = () => {
    if (pagination && pagination.currentPage < pagination.totalPages) {
      fetchFeedbacks(pagination.currentPage + 1);
    }
  };

  const hasMore = pagination ? pagination.currentPage < pagination.totalPages : false;

  const handleToggleStatus = (key: string) => {
    setFeedbacks((prev) =>
      prev.map((feedback) => {
        const feedbackKey = `${feedback.surveyOriginalId}|${feedback.version}`;
        if (feedbackKey === key) {
          return {
            ...feedback,
            status: feedback.status === "New" ? "Reviewed" : "New",
          };
        }
        return feedback;
      })
    );
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };

  const handleFeedbackClick = (feedback: GroupedFeedbackSession) => {
    setSelectedFeedback(feedback);
  };

  const handleBackToList = () => {
    setSelectedFeedback(null);
  };

  // If a feedback is selected, show the detail view
  if (selectedFeedback) {
    return (
      <FeedbackDetailView
        group={selectedFeedback}
        onBack={handleBackToList}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ maxWidth: "1120px", mx: "auto", mt: 4, px: 3, textAlign: "center", py: 8 }}>
        <CircularProgress size={40} sx={{ color: derinColors.primaryDark }} />
        <Typography sx={{ mt: 2, color: derinColors.textSecondary }}>
          Loading feedback sessions...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ maxWidth: "1120px", mx: "auto", mt: 4, px: 3, textAlign: "center", py: 8 }}>
        <Typography sx={{ color: derinColors.textPrimary, mb: 1 }}>
          {error}
        </Typography>
        <Button
          onClick={() => fetchFeedbacks(1)}
          sx={{
            background: derinColors.primaryDark,
            color: derinColors.textWhite,
            textTransform: "none",
            "&:hover": { background: derinColors.hoverButtonDark },
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1120px", mx: "auto", mt: 4, px: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 500,
            color: derinColors.textPrimary,
            mb: 0.5,
          }}
        >
          Task List
        </Typography>
        <Typography
          sx={{
            ...derinTypography.body,
            color: derinColors.textSecondary,
          }}
        >
          Review client feedback submissions across all surveys.
        </Typography>
      </Box>

      {/* Main Card */}
      <Box
        sx={{
          background: derinColors.backgroundWhite,
          border: `1px solid ${derinColors.borderLight}`,
          borderRadius: derinSpacing.cardBorderRadius,
          padding: "24px",
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              ...derinTypography.heading,
              color: derinColors.textPrimary,
            }}
          >
            All Feedback Submissions
          </Typography>

          {/* Filters */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* User Name Search */}
            <Box sx={derinInputStyles.searchContainer}>
              <PersonOutlinedIcon sx={{ color: derinColors.textSecondary, fontSize: 16, mr: 1 }} />
              <InputBase
                placeholder="Search by user name..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                sx={derinInputStyles.searchInput}
              />
            </Box>

            {/* Status Dropdown */}
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                ...derinInputStyles.select,
                width: "160px",
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Reviewed">Reviewed</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Feedback Cards List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {feedbacks.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                color: derinColors.textSecondary,
              }}
            >
              <Typography>No feedback submissions found</Typography>
            </Box>
          ) : (
            <>
            {feedbacks.map((feedback) => {
              const feedbackKey = `${feedback.surveyOriginalId}|${feedback.version}`;
              return (
              <Box
                key={feedbackKey}
                onClick={() => handleFeedbackClick(feedback)}
                sx={{
                  border: `1px solid ${derinColors.borderLight}`,
                  borderRadius: derinSpacing.containerBorderRadius,
                  background: "transparent",
                  minHeight: "145px",
                  padding: "20px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    border: `1px solid ${derinColors.borderDark}`,
                    background: derinColors.hoverBackground,
                  },
                }}
              >
                {/* Row 1 - Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                    {/* Status Badge */}
                    <Box
                      sx={{
                        background:
                          feedback.status === "New" ? derinColors.primaryDark : derinColors.badgeInactive,
                        color:
                          feedback.status === "New" ? derinColors.textWhite : derinColors.primaryDark,
                        ...derinTypography.badge,
                        px: 1,
                        py: 0.25,
                        borderRadius: derinSpacing.buttonBorderRadius,
                        flexShrink: 0,
                      }}
                    >
                      {feedback.status}
                    </Box>

                    {/* Separator */}
                    <Typography sx={{ color: derinColors.textSecondary, flexShrink: 0 }}>
                      •
                    </Typography>

                    {/* Survey Icon + Name */}
                    <DescriptionOutlinedIcon
                      sx={{ color: derinColors.textSecondary, fontSize: 16, flexShrink: 0 }}
                    />
                    <Typography
                      sx={{
                        ...derinTypography.body,
                        color: derinColors.textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {feedback.surveyName}
                    </Typography>

                    {/* Version Badge */}
                    <Box
                      sx={{
                        border: `1px solid ${derinColors.borderLight}`,
                        borderRadius: derinSpacing.buttonBorderRadius,
                        ...derinTypography.badge,
                        color: derinColors.textPrimary,
                        px: 1,
                        py: 0.25,
                        flexShrink: 0,
                      }}
                    >
                      v{feedback.version}
                    </Box>
                  </Box>

                  {/* Action Button */}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleStatus(feedbackKey);
                    }}
                    sx={{
                      background:
                        feedback.status === "New" ? derinColors.primaryDark : derinColors.backgroundWhite,
                      color:
                        feedback.status === "New" ? derinColors.textWhite : derinColors.textPrimary,
                      border:
                        feedback.status === "New"
                          ? "none"
                          : `1px solid ${derinColors.borderLight}`,
                      borderRadius: derinSpacing.buttonBorderRadius,
                      height: derinSpacing.buttonHeight,
                      ...derinTypography.button,
                      textTransform: "none",
                      pl: feedback.status === "New" ? 1.5 : 2,
                      pr: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                      "&:hover": {
                        background:
                          feedback.status === "New" ? derinColors.hoverButtonDark : derinColors.hoverButtonLight,
                      },
                    }}
                  >
                    {feedback.status === "New" && (
                      <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                    )}
                    {feedback.status === "New"
                      ? "Mark as Reviewed"
                      : "Mark as New"}
                  </Button>
                </Box>

                {/* Content Grid - 2 columns */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: `${derinSpacing.headerGap} 24px`,
                  }}
                >
                  {/* Row 1 - Organization */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BusinessOutlinedIcon
                      sx={{ color: derinColors.textSecondary, fontSize: 16 }}
                    />
                    <Typography
                      sx={{
                        ...derinTypography.body,
                        color: derinColors.textPrimary,
                      }}
                    >
                      {feedback.organizationName}
                    </Typography>
                  </Box>

                  {/* Row 1 - Users */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonOutlinedIcon
                      sx={{ color: derinColors.textSecondary, fontSize: 16 }}
                    />
                    <Typography
                      sx={{
                        ...derinTypography.body,
                        color: derinColors.textPrimary,
                      }}
                    >
                      {feedback.submittedBy.join(", ")}
                    </Typography>
                  </Box>

                  {/* Row 2 - Date */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon sx={{ color: derinColors.textSecondary, fontSize: 16 }} />
                    <Typography
                      sx={{
                        ...derinTypography.body,
                        color: derinColors.textPrimary,
                      }}
                    >
                      {formatDate(feedback.lastSubmittedAt)}
                    </Typography>
                  </Box>

                  {/* Row 2 - Comments */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ChatBubbleOutlineIcon
                      sx={{ color: derinColors.textSecondary, fontSize: 16 }}
                    />
                    <Typography
                      sx={{
                        ...derinTypography.body,
                        color: derinColors.textPrimary,
                      }}
                    >
                      {feedback.totalComments} Comments
                    </Typography>
                  </Box>
                </Box>
              </Box>
              );
            })}

            {/* Load More Button */}
            {hasMore && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  sx={{
                    border: `1px solid ${derinColors.borderLight}`,
                    borderRadius: derinSpacing.buttonBorderRadius,
                    height: derinSpacing.buttonHeight,
                    ...derinTypography.button,
                    color: derinColors.textPrimary,
                    textTransform: "none",
                    px: 3,
                    "&:hover": {
                      background: derinColors.hoverButtonLight,
                    },
                  }}
                >
                  {loadingMore ? (
                    <CircularProgress size={18} sx={{ color: derinColors.primaryDark, mr: 1 }} />
                  ) : null}
                  {loadingMore ? "Loading..." : "Load More"}
                </Button>
              </Box>
            )}

            {/* Pagination info */}
            {pagination && (
              <Typography
                sx={{
                  ...derinTypography.body,
                  color: derinColors.textSecondary,
                  textAlign: "center",
                  mt: 1,
                }}
              >
                Showing {feedbacks.length} submissions
              </Typography>
            )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FeedbackSection;
